/**
 * Turn a provider's raw failure into something a person can act on.
 *
 * The providers throw with the API's own words — `Drive API error (403): {
 * "error": { "code": 403, "message": "Google Drive API has not been used in
 * project 5983… before or it is disabled…` — and that string was being pasted
 * straight into the banner at the top of the app. Users got a wall of JSON,
 * cut off mid-sentence by the banner's width, describing a problem only the
 * developer can fix.
 *
 * Every branch here answers two questions: what went wrong, and whose move it
 * is. When we don't recognise the failure we still never dump JSON — the
 * fallback extracts the API's own `message` field if there is one and caps the
 * length.
 */

export type SyncProviderName = 'google' | 'github';

export interface SyncErrorAdvice {
  /** One sentence, plain language, safe to show in a banner. */
  message: string;
  /**
   * True when signing out and back in is the fix, so the caller can offer
   * that rather than leaving the user staring at a banner.
   */
  needsReauth: boolean;
}

/** Grab the API's own human-readable message out of a JSON error body. */
function apiMessage(raw: string): string | null {
  const match = raw.match(/"message"\s*:\s*"((?:[^"\\]|\\.)*)"/);
  if (!match) return null;
  const message = match[1].replace(/\\"/g, '"').replace(/\\n/g, ' ').trim();
  return message.length > 0 ? message : null;
}

function truncate(text: string, max = 160): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  // Cut at a word boundary so it doesn't end mid-word.
  return `${clean.slice(0, max).replace(/\s+\S*$/, '')}…`;
}

export function describeSyncSetupError(
  error: unknown,
  provider: SyncProviderName,
): SyncErrorAdvice {
  const raw = error instanceof Error ? error.message : String(error ?? '');
  const lower = raw.toLowerCase();
  const providerLabel = provider === 'google' ? 'Google Drive' : 'GitHub';

  // ── Whoever set the app up hasn't finished ────────────────────────────
  // A 403 saying the API "has not been used in project N before or it is
  // disabled" means the Cloud project doesn't have the Drive API switched
  // on. Nothing the user can do, and worth saying so plainly rather than
  // implying they broke something.
  if (
    lower.includes('has not been used in project') ||
    lower.includes('accessnotconfigured') ||
    lower.includes('api has not been enabled')
  ) {
    return {
      message:
        `${providerLabel} sync isn't switched on for this app yet — that's a setup step on our ` +
        `side, not something you did. Your data is safe on this device and the app works ` +
        `normally offline; try syncing again later.`,
      needsReauth: false,
    };
  }

  // Google's OAuth client is still in testing and this account isn't listed.
  if (lower.includes('access_denied') || lower.includes('has not completed the google verification')) {
    return {
      message:
        'This account is not on the app\'s tester list yet, so Google blocked the connection. ' +
        'Ask for access, or keep using the app on this device without signing in.',
      needsReauth: false,
    };
  }

  // ── The user's session ────────────────────────────────────────────────
  if (
    lower.includes('bad credentials') ||
    lower.includes('401') ||
    lower.includes('invalid_grant') ||
    lower.includes('expired') ||
    lower.includes('unauthenticated')
  ) {
    return {
      message: `Your ${providerLabel} session has expired. Sign out and back in to resume syncing.`,
      needsReauth: true,
    };
  }

  // Consent was granted for less than the app needs (or was partially revoked).
  if (lower.includes('insufficient') || lower.includes('insufficientpermissions')) {
    return {
      message:
        `TrackMind doesn't have permission to write to your ${providerLabel} yet. Sign out and ` +
        `back in, and accept the storage permission when asked.`,
      needsReauth: true,
    };
  }

  // ── Transient ─────────────────────────────────────────────────────────
  if (lower.includes('rate limit') || lower.includes('429') || lower.includes('quota')) {
    return {
      message: `${providerLabel} is rate-limiting us at the moment. Syncing will resume shortly — nothing is lost.`,
      needsReauth: false,
    };
  }

  if (
    lower.includes('failed to fetch') ||
    lower.includes('networkerror') ||
    lower.includes('network request failed') ||
    lower.includes('offline')
  ) {
    return {
      message: `Couldn't reach ${providerLabel}. You're offline or it's unreachable — everything still saves on this device and will sync when the connection is back.`,
      needsReauth: false,
    };
  }

  if (lower.includes('500') || lower.includes('502') || lower.includes('503')) {
    return {
      message: `${providerLabel} is having trouble right now. Your data is safe on this device; syncing will retry.`,
      needsReauth: false,
    };
  }

  // ── Anything else: the API's own sentence, never its JSON ─────────────
  const detail = apiMessage(raw) ?? raw;
  return {
    message: `Couldn't set up ${providerLabel} sync: ${truncate(detail)} You can keep using the app offline.`,
    needsReauth: false,
  };
}
